import { useState } from "react";
import SectionCard from "./SectionCard";

type Props = {
  onSaved: (msg: string) => void;
};

const initial = {
  companyName: "PureNest",
  legalName: "PureNest Cleaning Co.",
  email: "hello@purenest.co",
  phone: "(347) 555-0148",
  timezone: "America/New_York",
  currency: "USD ($)",
};

const days = [
  { id: "mon", label: "Mon" },
  { id: "tue", label: "Tue" },
  { id: "wed", label: "Wed" },
  { id: "thu", label: "Thu" },
  { id: "fri", label: "Fri" },
  { id: "sat", label: "Sat" },
  { id: "sun", label: "Sun" },
];

const initialHours: Record<string, { open: string; close: string; on: boolean }> = {
  mon: { open: "08:00", close: "18:00", on: true },
  tue: { open: "08:00", close: "18:00", on: true },
  wed: { open: "08:00", close: "18:00", on: true },
  thu: { open: "08:00", close: "18:00", on: true },
  fri: { open: "08:00", close: "18:00", on: true },
  sat: { open: "09:00", close: "15:00", on: true },
  sun: { open: "10:00", close: "14:00", on: false },
};

export default function BusinessSection({ onSaved }: Props) {
  const [form, setForm] = useState(initial);
  const [hours, setHours] = useState(initialHours);

  return (
    <div className="space-y-5">
      <SectionCard
        title="Company info"
        description="What customers see on receipts, emails and your storefront."
        footer={
          <>
            <button
              onClick={() => setForm(initial)}
              className="px-4 py-2 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              Reset
            </button>
            <button
              onClick={() => onSaved("Company info saved")}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-primary-500 hover:bg-primary-600 text-background-50 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-line"></i>
              Save changes
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              Company name
            </label>
            <input
              type="text"
              value={form.companyName}
              onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
              className="w-full bg-background-100 text-sm text-foreground-900 rounded-md px-3 py-2.5 outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              Legal name
            </label>
            <input
              type="text"
              value={form.legalName}
              onChange={(e) => setForm((f) => ({ ...f, legalName: e.target.value }))}
              className="w-full bg-background-100 text-sm text-foreground-900 rounded-md px-3 py-2.5 outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              Support email
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="w-full bg-background-100 text-sm text-foreground-900 rounded-md px-3 py-2.5 outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              Phone
            </label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              className="w-full bg-background-100 text-sm text-foreground-900 rounded-md px-3 py-2.5 outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              Timezone
            </label>
            <select
              value={form.timezone}
              onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
              className="w-full bg-background-100 text-sm text-foreground-900 rounded-md px-3 py-2.5 outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300 cursor-pointer"
            >
              <option>America/New_York</option>
              <option>America/Chicago</option>
              <option>America/Denver</option>
              <option>America/Los_Angeles</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              Currency
            </label>
            <select
              value={form.currency}
              onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
              className="w-full bg-background-100 text-sm text-foreground-900 rounded-md px-3 py-2.5 outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300 cursor-pointer"
            >
              <option>USD ($)</option>
              <option>CAD ($)</option>
              <option>EUR (€)</option>
              <option>GBP (£)</option>
            </select>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Business hours"
        description="When customers can book new jobs through your site."
        footer={
          <button
            onClick={() => onSaved("Hours updated")}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-primary-500 hover:bg-primary-600 text-background-50 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-check-line"></i>
            Save hours
          </button>
        }
      >
        <ul className="divide-y divide-background-200">
          {days.map((d) => {
            const h = hours[d.id];
            return (
              <li key={d.id} className="flex items-center gap-3 py-3">
                <button
                  onClick={() =>
                    setHours((all) => ({
                      ...all,
                      [d.id]: { ...all[d.id], on: !all[d.id].on },
                    }))
                  }
                  className={`w-10 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                    h.on ? "bg-primary-500" : "bg-background-200"
                  }`}
                  aria-label={`Toggle ${d.label}`}
                >
                  <span
                    className={`block w-5 h-5 rounded-full bg-background-50 transition-transform mt-0.5 ${
                      h.on ? "translate-x-[18px]" : "translate-x-0.5"
                    }`}
                  ></span>
                </button>
                <span className="text-sm font-medium text-foreground-950 w-12">
                  {d.label}
                </span>
                {h.on ? (
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      type="time"
                      value={h.open}
                      onChange={(e) =>
                        setHours((all) => ({
                          ...all,
                          [d.id]: { ...all[d.id], open: e.target.value },
                        }))
                      }
                      className="bg-background-100 text-sm text-foreground-900 rounded-md px-2 py-1.5 outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300"
                    />
                    <span className="text-xs text-foreground-500">to</span>
                    <input
                      type="time"
                      value={h.close}
                      onChange={(e) =>
                        setHours((all) => ({
                          ...all,
                          [d.id]: { ...all[d.id], close: e.target.value },
                        }))
                      }
                      className="bg-background-100 text-sm text-foreground-900 rounded-md px-2 py-1.5 outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300"
                    />
                  </div>
                ) : (
                  <span className="text-xs text-foreground-500 italic">Closed</span>
                )}
              </li>
            );
          })}
        </ul>
      </SectionCard>
    </div>
  );
}